import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { App } from './app/app';
import { Playcast } from './app/Playcast';
import { getPersistentPlayerId } from './api';
import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import constants from '@playcastdotio/GlobalConfig';
import * as Sentry from '@sentry/react';
import config from './config.json';
import ReactGA from 'react-ga4';

const trackingId = import.meta?.env?.VITE_GA_TRACKING_ID || constants.GA_TRACKING_ID;

if (trackingId) {
  console.log('Google Analytics tracking ID set', trackingId);
  // Check for a clerk user ID and use that as the user_id for GA if it exists
  const clerkUserId = localStorage.getItem('clerkUserID');
  const persistentId = getPersistentPlayerId();
  let user_id = persistentId || '';
  if (clerkUserId && user_id !== clerkUserId && clerkUserId !== '') {
    user_id = clerkUserId;
  }

  const debug_mode = !(constants.VERSION.split('-')[1] === undefined);
  if (debug_mode) {
    ReactGA.initialize(trackingId, {
      gaOptions: {
        debug_mode,
      },
      gtagOptions: {
        debug_mode,
      },
    });
  } else {
    ReactGA.initialize(trackingId);
  }

  ReactGA.set({
    version: constants.VERSION,
    environment: constants.ENVIRONMENT,
    user_id,
  });

  // With this:
  ReactGA.send({
    hitType: 'pageview',
    page: window.location.pathname,
    title: document.title,
  });

  // And add an explicit page_view event:
  ReactGA.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
  (window as any).ReactGA = ReactGA;
} else {
  console.warn('Google Analytics tracking ID not set');
}

if (!constants.VERSION.endsWith('-local')) {
  Sentry.init({
    release: constants.VERSION,
    environment: constants.ENVIRONMENT,
    dsn: config.SENTRY_DSN,
    normalizeDepth: 10,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost', // Enable distributed tracing for localhost
      /^https:\/\/.*\.playcast\.io(?!.*clerk)/, // Enable distributed tracing for all playcast.io URLs except clerk
      /^https:\/\/.*\.gibber\.com(?!.*clerk)/, // Enable distributed tracing for all gibber.io URLs except clerk
      /^https:\/\/.*\.api\.gibber\.com/, // Enable distributed tracing for all api.gibber.com URLs
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  if (typeof window !== 'undefined') {
    (window as any).Sentry = Sentry;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
const PUBLISHABLE_KEY = constants.CLERK_PUBLIC_KEY;
const branchSplit = constants.VERSION.split('-')[1];
const isClerkProd = branchSplit === undefined || branchSplit === 'prerelease';

// Force the playerId immediately on any page load
getPersistentPlayerId();

root.render(
  <Playcast>
    <StrictMode>
      {isClerkProd ? (
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          appearance={{
            baseTheme: dark,
            elements: {
              formFieldInput: 'text-midnight-white text-sm bg-midnight-darker !py-6 rounded-lg',
              otpCodeFieldInput: 'text-gray-900 text-sm',
              socialButtonsIconButton: 'bg-midnight !border !border-midnight-bright !py-4',
              dividerLine: 'bg-midnight h-0.5',
              footer: 'no-bg !bg-midnight font-semibold',
              footerActionText: 'text-midnight-white font-semibold opacity-40',
              footerActionLink: 'text-amber-400 hover:text-amber-400 hover:opacity-60',
            },
            variables: {
              colorBackground: '#1B4366',
              colorPrimary: '#fbbf24',
              colorText: '#f2f2f2',
              colorTextOnPrimaryBackground: '#000000',
              colorTextSecondary: '#DEFFFF',
            },
          }}
          afterSignOutUrl={window.location.href}
          signInForceRedirectUrl={window.location.href}
          signUpForceRedirectUrl={window.location.href}
        >
          <App />
        </ClerkProvider>
      ) : (
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          appearance={{
            baseTheme: dark,
            elements: {
              formFieldInput: 'text-midnight-white text-sm bg-midnight-darker !py-6 rounded-lg',
              otpCodeFieldInput: 'text-gray-900 text-sm',
              socialButtonsIconButton: 'bg-midnight !border !border-midnight-bright !py-4',
              dividerLine: 'bg-midnight h-0.5',
              footer: 'no-bg !bg-midnight font-semibold',
              footerActionText: 'text-midnight-white font-semibold opacity-40',
              footerActionLink: 'text-amber-400 hover:text-amber-400 hover:opacity-60',
            },
            variables: {
              colorBackground: '#1B4366',
              colorPrimary: '#fbbf24',
              colorText: '#f2f2f2',
              colorTextOnPrimaryBackground: '#000000',
              colorTextSecondary: '#DEFFFF',
            },
          }}
          afterSignOutUrl={window.location.href}
          signInForceRedirectUrl={window.location.href}
          signUpForceRedirectUrl={window.location.href}
        >
          <App />
        </ClerkProvider>
      )}
    </StrictMode>
  </Playcast>,
);

